import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { PieChart, Pie, Tooltip, Cell } from 'recharts';
import styled from 'styled-components';
import { uniq, intersection, flatten } from 'lodash';
import ContainerDimensions from 'react-container-dimensions';
import Settings from './Settings';
import Loader from '../../../general/Loader';
import {
  availabilityCodeToTasksMap,
  loading as availabilityRequirementSchedulesLoading
} from '../../../../state/modules/availability-requirement-schedules/selectors';

const PieChartContainer = styled.div`
  width: 100%;
  height: 90%;
  position: relative;
`;

const ResponseLevelPieChart = ({
  groupId,
  groupIds,
  groups,
  availabilityCodeToTasksMap,
  availabilityRequirementSchedulesLoading,
  tasks,
  dashboardAPIManager
}) => {
  useEffect(() => {
    dashboardAPIManager.fetchAvailabilityCodes();
    dashboardAPIManager.fetchTasks();
    // register for updates to availability requirements
    dashboardAPIManager.registerForAvailabilityRequirementScheduleUpdates();
  }, [dashboardAPIManager]);

  // Get all the relevant group ids, including descendants of chosen groups
  // remove duplicates
  const allRelevantGroupIds = useMemo(() => {
    const groupsById = (groups || {}).byId;
    // If no group has been chosen, return current group and descendants
    if (groupIds.length === 0) return [groupId, ...groupsById[groupId].descendant_ids];
    // return all descendant ids from the chosen groups
    return uniq(
      flatten(
        groupIds.map(id => {
          if (!groupsById[id]) return;
          return [id, ...groupsById[id].descendant_ids];
        })
      )
    );
  }, [groupIds, groupId, groups]);

  const chartData = useMemo(() => {
    return Object.values(availabilityCodeToTasksMap).map(info => {
      if (!info) return {};
      const { availabilityCode, tasks } = info;
      // Filter out tasks that are not relevant to the chosen groups
      // Filter tasks by the relevant group ids
      const filteredTasks = tasks.filter(task => intersection(task.group_ids, allRelevantGroupIds).length > 0);

      const value = filteredTasks.length;
      const label = availabilityCode.name;
      const color = availabilityCode.html_color;

      return {
        value,
        color,
        // highlight: color,
        name: label
      };
    });
  }, [availabilityCodeToTasksMap, allRelevantGroupIds, tasks]);

  if (availabilityRequirementSchedulesLoading || !groups.lastFetch || !tasks.lastFetch) {
    return <Loader />;
  }

  // Get the groups that are being displayed in the piechart, this allows generating
  // the banner indicating the groups.
  const defaultGroup = groups.byId[groupId];
  const displayedGroups = groupIds.map(id => groups.byId[id]).filter(Boolean);

  return (
    <>
      {displayedGroups.length > 0 ? (
        displayedGroups.map((g, index) => (
          <span key={index}>
            {index > 0 && ', '}
            {g.name}
          </span>
        ))
      ) : (
        <span>{defaultGroup.name}</span>
      )}
      <PieChartContainer>
        <ContainerDimensions>
          {({ width, height }) => (
            <PieChart width={width} height={height}>
              <Pie dataKey="value" isAnimationActive={false} data={chartData} label>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          )}
        </ContainerDimensions>
      </PieChartContainer>
    </>
  );
};

const mapStateToProps = state => ({
  groups: state.groups,
  tasks: state.tasks,
  availabilityRequirementSchedulesLoading: availabilityRequirementSchedulesLoading(state),
  availabilityCodeToTasksMap: availabilityCodeToTasksMap(state)
});

Object.assign(ResponseLevelPieChart, {
  SettingsContent: Settings,
  defaultSettings: { groupIds: [] }
});

export default connect(mapStateToProps)(ResponseLevelPieChart);
